import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

interface UserPreferences {
  enabledTabs: string[];
  defaultBlotterFilters: Record<string, any>;
  theme: 'dark' | 'light';
  preferences: Record<string, any>;
}

interface UserPreferencesContextType {
  preferences: UserPreferences | null;
  loading: boolean;
  updatePreferences: (updates: Partial<UserPreferences>) => Promise<void>;
  refreshPreferences: () => Promise<void>;
}

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
};

interface UserPreferencesProviderProps {
  children: ReactNode;
}

export const UserPreferencesProvider: React.FC<UserPreferencesProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchPreferences = async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get('/api/user-preferences');
      setPreferences(response.data);
    } catch (error: any) {
      console.error('Failed to load user preferences:', error);
      // Set defaults if API fails
      setPreferences({
        enabledTabs: [],
        defaultBlotterFilters: {},
        theme: 'dark',
        preferences: {}
      });
    } finally {
      setLoading(false);
    }
  };

  const updatePreferences = async (updates: Partial<UserPreferences>) => {
    try {
      const response = await axios.put('/api/user-preferences', updates);
      setPreferences(response.data);
    } catch (error: any) {
      console.error('Failed to update user preferences:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchPreferences();
    }
  }, [isAuthenticated]);

  const value: UserPreferencesContextType = {
    preferences,
    loading,
    updatePreferences,
    refreshPreferences: fetchPreferences
  };

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
};
